import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { ParamEditor } from "./AnthropometricEditor";
import AnthropometricService from "./AnthropometricService";
import { PARAMETER_BY_KEY } from "./data";

/**
 * Transcript-facing thin wrapper around the standalone rail's ParamEditor
 * (see AnthropometricEditor.js). Renders one card per captured
 * measurement in the transcript flow: outer chrome + theme + eyebrow,
 * body delegated to ParamEditor.
 *
 * Two seed paths:
 *   1. AI-captured payload carries the value (numericValue OR
 *      valueDisplay). ParamEditor is seeded from the payload; save is
 *      always enabled (`bypassSameAsLatest=true`) so the reviewer's Save
 *      commits the AI-seeded values verbatim.
 *   2. Manual row-click carries only `{parameter}`. Fetch the patient's
 *      current anthropometric doc and seed ParamEditor with the
 *      parameter's persisted Current — matches the standalone rail's
 *      pre-fill so the reviewer starts from the known value, and the
 *      "no changes to save" gate stays on (same as standalone Edit).
 *
 * Standalone rail is unchanged — it continues to embed ParamEditor
 * inside its own accordion card and passes `latestOfThis` from its
 * own fetched doc.
 */

const findParameter = (parameters, key) => {
  for (const p of parameters || []) {
    if (p.parameter === key) return p;
  }
  return null;
};

const AnthropometricForm = ({
  formId,
  patientId,
  initialPayload,
  saving,
  error,
  onSave,
  onClose,
  themeClass,
}) => {
  const p = initialPayload || {};
  const parameterKey = p.parameter;
  const meta = PARAMETER_BY_KEY[parameterKey];

  // "AI-seeded" = the payload carries a real measurement value. When
  // it does, ParamEditor is seeded from the payload and Save is always
  // enabled (the reviewer's commit IS the change).
  const hasAiValue =
    (typeof p.numericValue === "number" && !Number.isNaN(p.numericValue)) ||
    (typeof p.valueDisplay === "string" && p.valueDisplay.trim() !== "");

  // Manual row-click needs the persisted Current — fetch once on mount.
  // The fetch is skipped for AI-seeded cards (their `latestOfThis` is
  // derived from the payload) and for the "unknown parameter" bail
  // branch below.
  const [fetchedCurrent, setFetchedCurrent] = useState(null);
  const [fetching, setFetching] = useState(!hasAiValue && !!patientId && !!meta);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    if (hasAiValue || !patientId || !meta) return;
    const svc = new AnthropometricService();
    svc.list(
      patientId,
      (doc) => {
        if (!mounted.current) return;
        const entry = findParameter(doc && doc.parameters, parameterKey);
        setFetchedCurrent(entry ? entry.current : null);
        setFetching(false);
      },
      () => {
        if (!mounted.current) return;
        setFetchedCurrent(null);
        setFetching(false);
      }
    );
  }, [hasAiValue, patientId, meta, parameterKey]);

  if (!meta) {
    return (
      <div className="an-acc">
        <div className={`an-card is-open ${themeClass || ""}`.trim()}>
          <div className="an-card-body">
            <p className="cc-feedback is-err">
              Unknown anthropometric parameter — cannot render editor.
            </p>
            <div className="sec-editor-actions an-editor-actions">
              <button type="button" className="sec-btn sec-btn-ghost" onClick={() => onClose(formId)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Seed source: AI payload wins; else the fetched persisted Current.
  // Missing on both paths → null (blank card).
  let latestOfThis = null;
  if (hasAiValue) {
    latestOfThis = {
      numericValue: (typeof p.numericValue === "number" ? p.numericValue : null),
      unit: p.unit || "",
      valueDisplay: p.valueDisplay || "",
      testDate: p.testDate ? new Date(p.testDate).toISOString() : new Date().toISOString(),
    };
  } else if (fetchedCurrent) {
    latestOfThis = fetchedCurrent;
  }


  const rootClass = ["an-card", "is-open", themeClass || ""].filter(Boolean).join(" ");
  const displayError = error || "";

  const handleSave = (body) => {
    if (typeof onSave === "function") onSave(body);
  };
  const handleClose = () => onClose(formId);

  return (
    <div className="an-acc">
      <div className={rootClass}>
        {/* Same shape as the standalone rail's accordion head — kept as
            a button so `.an-acc .an-card-head` styling applies. The
            transcript card is always open, so the click is a no-op
            (the reviewer dismisses via Cancel in the footer). */}
        <button
          type="button"
          className="an-card-head"
          aria-expanded="true"
          onClick={() => {}}
          style={{ cursor: "default" }}
        >
          <span className="an-card-title">{meta.label}</span>
        </button>
        {fetching ? (
          <div className="an-card-body">
            <p className="ait-rail-load">Loading current value…</p>
          </div>
        ) : (
          <ParamEditor
            meta={meta}
            latestOfThis={latestOfThis}
            onSave={handleSave}
            onCancel={handleClose}
            submitting={!!saving}
            bypassSameAsLatest={hasAiValue}
            menuClassName="ait-menu"
            saveLabel="Add"
            savingLabel="Adding…"
          />
        )}
        {displayError && (
          <div className="cc-feedback is-err an-feedback">{displayError}</div>
        )}
      </div>
    </div>
  );
};

AnthropometricForm.propTypes = {
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  patientId: PropTypes.number,
  initialPayload: PropTypes.object,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  themeClass: PropTypes.string,
};

AnthropometricForm.defaultProps = {
  patientId: undefined,
  initialPayload: null,
  saving: false,
  error: "",
  themeClass: "",
};

export default AnthropometricForm;
